import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Toaster, toast } from 'sonner';
import { Eye, EyeOff, Sparkles, UserPlus } from 'lucide-react';
import Navbar from '../components/Navbar';

const Signup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create the account
      await api.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        role: 'parent'
      });

      // GTM conversion event — account has been created at this point
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'registration_complete' });
      
      // Auto-login after registration
      await login(formData.email, formData.password);
      
      toast.success('Account created successfully!');
      
      // Check for redirect parameter
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-xl border-[#333] bg-[#111]">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#FF007F] to-[#7000FF] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="font-heading text-2xl text-white">Create Account</CardTitle>
            <CardDescription className="text-white/60">Join UPA to enrol your child</CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-white/80">First Name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="h-11 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                    data-testid="signup-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-white/80">Last Name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="h-11 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                    data-testid="signup-lastname"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-11 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                  data-testid="signup-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/80">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+44 7XXX XXXXXX"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="h-11 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                  data-testid="signup-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 8 characters"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="h-11 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40 pr-10"
                    data-testid="signup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="h-11 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                  data-testid="signup-confirm-password"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-11 bg-gradient-to-r from-[#FF007F] to-[#7000FF] hover:opacity-90 text-white font-semibold"
                disabled={loading}
                data-testid="signup-submit"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-white/50">
                Already have an account?{' '}
                <Link 
                  to={searchParams.get('redirect') ? `/login?redirect=${searchParams.get('redirect')}` : '/login'}
                  className="text-[#FF007F] hover:underline font-medium"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
